import {Typography} from '@mui/material';
import classNames, {Argument} from 'classnames';
import * as React from 'react';
import {useCallback} from 'react';

import {DlMuiPopper} from './DlMuiPopper';
import styles from './IconButton.less';
import {ClickItem} from 'src/js/common/types';
import {useMuiAnchor} from 'src/js/hooks';

interface OwnProps {
  disabled?: boolean;
  dataAttr?: string;
  dataName?: string;
  item?: ClickItem;
  titleText?: string;
  iconClass: string;
  className?: Argument;
  clickCallback: (item?: ClickItem) => void;
}

type Props = OwnProps;

export const IconButton: React.FC<Props> = (props) => {
  const {
    className = '',
    clickCallback,
    dataName,
    dataAttr,
    disabled = false,
    iconClass,
    item,
    titleText = '',
  } = props;

  const hasTooltip = !!titleText;

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} =
    useMuiAnchor(hasTooltip);

  const performAction = useCallback(
    (event) => {
      event.stopPropagation();
      if (disabled) {
        event.preventDefault();
      } else {
        clickCallback(item);
      }
    },
    [clickCallback, item, disabled]
  );

  const handleClick = useCallback(
    (event) => {
      event.stopPropagation();
      performAction(event);
    },
    [performAction]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        performAction(event);
      }
      return true;
    },
    [performAction]
  );

  return (
    <>
      <i
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role={'button'}
        className={classNames(className, {
          [iconClass]: true,
          [styles.dlIconButton]: true,
          [styles.dlDisabled]: disabled,
        })}
        data-name={dataName}
        data-attr={dataAttr}
      />
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{titleText}</Typography>
      </DlMuiPopper>
    </>
  );
};

IconButton.displayName = 'IconButton';
